import * as Sentry from '@sentry/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import toaster from 'react-hot-toast';
import { addLaundryCatCustomerSyncJob } from 'components/admin/businesses/LaundroworksIntegration/addLaundroworksCustomerSyncJob';
import {
  ActiveJob,
  getLaundryCatActiveJobs,
} from 'components/admin/businesses/LaundroworksIntegration/getLaundroworksActiveJobs';
import { AddSyncJobPayload, JobScope } from './useLaundroworksCustomerSyncJobs';

export const useGetLaundryCatActiveJobs = (businessId: number) => {
  const { isLoading, error, data, refetch } = useQuery<
    unknown,
    AxiosError<{ error: string }>,
    ActiveJob[]
  >({
    queryKey: ['laundryCatActiveJobs', businessId],
    queryFn: async () => {
      const response = await getLaundryCatActiveJobs(businessId);
      return response.data?.jobs ?? [];
    },
    retry: 1,
    enabled: !!businessId,
    onError: (error) => {
      Sentry.captureException(error);
      toaster(error?.response?.data?.error ?? 'An error occurred while fetching LC active jobs', {
        icon: '❌',
      });
    },
  });

  return { isLoading, error, data, refetch };
};

export const useAddLaundryCatCustomerSyncJob = () => {
  return useMutation<unknown, AxiosError<{ error: string }>, AddSyncJobPayload>({
    mutationFn: async ({ businessId, scope }) => {
      return addLaundryCatCustomerSyncJob(businessId, scope);
    },
    onSuccess: async (_, variables) => {
      toaster(
        `${
          variables.scope === JobScope.FULL ? 'Full' : 'Incremental'
        } LC sync job added successfully`,
        {
          icon: '✅',
        },
      );
    },
    onError: (error) => {
      Sentry.captureException(error);
      toaster(error?.response?.data?.error ?? 'An error occurred while adding LC sync job', {
        icon: '❌',
      });
    },
  });
};
